import { useCallback, useEffect, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import { useAccount } from 'wagmi'
import { useStaking2aUserStakeData } from '@/hooks/staking2a-contract/useStaking2aUserStakeData'
import { CertificationRibbon } from '@/components/CertificationRibbon'
import { TopSection } from './TopSection'
import { DaoAnnouncementSection } from './DaoAnnouncementSection'
import { PepperIncStatsSection } from './PepperIncStatsSection'
import { BalanceCard } from './cards/BalanceCard'
import { StakedCard } from './cards/StakedCard'
import { WelcomeCard } from './cards/WelcomeCard'
import { UserPollCard } from './cards/UserPollCard'
import { useSurveys } from '@/data-access/surveys/queries'
import { Spinner } from '@/components/ui/Spinner'
import { hoursToMs } from '@/utils/datetime/unitToMs'
import type { Hex } from 'viem'
import { toast } from 'react-toastify'
import { useChunkedData } from '@/hooks/helpers/useChunkedData'

export default function PepperInc() {
	const { isConnected, address: userAddress } = useAccount()
	const { data: userStakedData } = useStaking2aUserStakeData()

	// Destructure user staked data with defaults
	const { totalUnstakable: userUnlockedTokens = 0n, totalLocked: userLockedTokens = 0n } = userStakedData ?? {}

	// Hooks for pagination
	const { ref: loadMoreRef, inView } = useInView({
		threshold: 0.15,
		triggerOnce: false
	})

	// Not caching the ongoing surveys
	const { data, refetch: refetchOngoing } = useSurveys({
		page: 1,
		// We will never have 10 ongoing surveys. Setting it to 10 for contingency.
		perPage: 10,
		state: ['ongoing'],
		orderBy: 'endDate',
		direction: 'asc'
	})

	// Not caching the upcoming surveys
	const { data: upcomingSurvey, refetch: refetchUpcoming } = useSurveys({
		page: 1,
		// We will never have 10 upcoming surveys. Setting it to 10 for contingency.
		perPage: 10,
		state: ['upcoming'],
		orderBy: 'startDate',
		direction: 'asc'
	})

	const {
		refetch: refetchEnded,
		data: endedSurveyData,
		fetchNextPage,
		isFetchingNextPage,
		hasNextPage
	} = useSurveys({
		page: 1,
		perPage: 3,
		staleTime: hoursToMs(1),
		state: ['ended'],
		orderBy: 'endDate',
		direction: 'desc'
	})

	const flatOngoingSurveys = useMemo(() => data?.pages.flatMap(page => page.surveys), [data])
	const flatEndedSurveys = useMemo(() => endedSurveyData?.pages.flatMap(page => page.surveys), [endedSurveyData])
	const flatUpcomingSurveys = useMemo(() => upcomingSurvey?.pages.flatMap(page => page.surveys), [upcomingSurvey])

	const [endedSurveyChunk, addEndedSurveyChunk] = useChunkedData(flatEndedSurveys ?? [], 5)

	// Effect to handle pagination when loadMore element comes into view
	useEffect(() => {
		if (inView && !isFetchingNextPage) {
			if (hasNextPage) {
				fetchNextPage()
			}

			if (endedSurveyChunk.length < (flatEndedSurveys?.length ?? 0)) {
				addEndedSurveyChunk()
			}
		}
	}, [inView, isFetchingNextPage, hasNextPage])

	const onSurveyEnded = useCallback(
		(addressPromise: Promise<Hex>) => {
			toast.promise(
				addressPromise.then(async () => {
					await Promise.all([refetchOngoing(), refetchEnded()])
				}),
				{
					pending: 'Refreshing Polls',
					success: 'Polls Updated',
					error: 'Something went wrong'
				}
			)
		},
		[refetchEnded, refetchOngoing]
	)

	return (
		<div className="mx-auto overflow-x-hidden bg-green-10">
			<TopSection />
			<DaoAnnouncementSection />
			<PepperIncStatsSection />
			<div className="lg:mx-[200px] my-10 mx-6 flex items-center flex-col gap-8">
				{!isConnected ? (
					<WelcomeCard className="max-w-[900px]" />
				) : (
					<div className="flex flex-col md:flex-row gap-5 justify-between max-w-[900px] w-full">
						<StakedCard className="flex-1" />
						<BalanceCard className="flex-1" />
					</div>
				)}
				<div className="max-w-[900px] w-full mb-4 flex flex-col gap-4 items-center" id="polls">
					{flatOngoingSurveys?.map((poll, idx) => (
						<UserPollCard
							key={`ongoing-${poll.surveyContract}`}
							testIdDiscriminator={`Ongoing${idx}`}
							address={poll.surveyContract}
							question={poll.question}
							startDate={poll.startDate}
							endDate={poll.endDate}
							lockedPepper={0n}
							userUnlockedTokens={userUnlockedTokens}
							userLockedTokens={userLockedTokens}
							isConnected={isConnected}
							userAddress={userAddress}
							votingCap={BigInt(poll.votingCap)}
							onEnded={onSurveyEnded}
							status={poll.status}
							className="max-w-[900px] w-full bg-green-40"
						/>
					))}
					{flatUpcomingSurveys?.map((poll, idx) => (
						<UserPollCard
							key={`upcoming-${poll.surveyContract}`}
							testIdDiscriminator={`Upcoming${idx}`}
							address={poll.surveyContract}
							question={poll.question}
							startDate={poll.startDate}
							endDate={poll.endDate}
							lockedPepper={0n}
							userUnlockedTokens={userUnlockedTokens}
							userLockedTokens={userLockedTokens}
							isConnected={isConnected}
							userAddress={userAddress}
							votingCap={BigInt(poll.votingCap)}
							status={poll.status}
							className="max-w-[900px] w-full bg-green-20"
						/>
					))}
					<div className="text-6xl font-bold text-neutral-100 text-start uppercase w-full">Ended Polls</div>
					{endedSurveyChunk?.map((poll, idx) => (
						<UserPollCard
							key={`ended-${idx}-${poll.surveyContract}`}
							testIdDiscriminator={`Ended${idx}`}
							address={poll.surveyContract}
							question={poll.question}
							startDate={poll.startDate}
							endDate={poll.endDate}
							lockedPepper={500_000_000_000_000_000_000n}
							isConnected={isConnected}
							userAddress={userAddress}
							votingCap={BigInt(poll.votingCap)}
							status={poll.status}
							className="max-w-[900px] w-full"
						/>
					))}
					{isFetchingNextPage && <Spinner size={24} className="mt-10" />}
					<div ref={loadMoreRef} className="w-full" />
				</div>
			</div>
			<CertificationRibbon />
		</div>
	)
}
